import { Graphics } from 'pixi.js';
import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Projectile, EnemyProjectile } from '../labyrinth/Projectile';
import type { SpriteBaker, BakedSprite } from './SpriteBaker';

const TEX_PX    = 192;
const Y_OFFSET  = 0.35;
const RENDER_ORDER = 20;

// Tier-indexed palettes — mirror LabyrinthState.ts
const BULLET_TRACER = [0xffdd44, 0xffee55, 0xaaddff, 0x66ccff, 0xcc88ff] as const;
const BULLET_HEAD   = [0xffffaa, 0xffffcc, 0xddeeff, 0xaaeeff, 0xeeccff] as const;
const BULLET_GLOW   = [0xff9900, 0xffaa11, 0x6699ff, 0x3388ff, 0xaa55ff] as const;
const ROCKET_BODY   = [0xff6633, 0xff7744, 0xff8855, 0xff9966, 0xffbb88] as const;
const ROCKET_CORE   = [0xffcc00, 0xffdd22, 0xffee44, 0xffffff, 0xffffff] as const;
const BOMB_SHELL    = [0x993300, 0xaa4411, 0xbb5522, 0xcc6633, 0xdd7744] as const;
const BOMB_CORE     = [0xff4400, 0xff5511, 0xff6622, 0xff8833, 0xffaa44] as const;
const BOMB_FUSE     = [0xffaa00, 0xffbb22, 0xffcc44, 0xffdd66, 0xffee88] as const;

// Per-kind bake-area half-extent (world px) — must cover the drawn trail/plume.
function halfExtentFor(kind: Projectile['kind'] | EnemyProjectile['kind'], isEnemy: boolean): number {
  if (isEnemy) return kind === 'bomb' ? TILE_SIZE * 2.5 : TILE_SIZE * 1.2;
  if (kind === 'bullet') return TILE_SIZE * 1.4;
  if (kind === 'rocket') return TILE_SIZE * 2.0;
  return TILE_SIZE * 2.5; // bomb
}

interface Entry {
  sprite: BakedSprite;
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  half: number;
}

/**
 * Per-projectile ground-plane billboard with a full 2D-style baked art:
 * multi-layer trails, glows, rocket plumes, bomb arcs, fuse sparks — ported
 * verbatim from LabyrinthState.drawProjectiles so visuals match 2D.
 */
export class ProjectileBillboards {
  readonly group = new THREE.Group();
  private readonly playerEntries = new Map<Projectile, Entry>();
  private readonly enemyEntries  = new Map<EnemyProjectile, Entry>();
  private readonly geoCache = new Map<number, THREE.PlaneGeometry>();

  constructor(private readonly baker: SpriteBaker) {}

  private getGeometry(half: number): THREE.PlaneGeometry {
    let geo = this.geoCache.get(half);
    if (!geo) {
      const size = half * 2;
      geo = new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2);
      this.geoCache.set(half, geo);
    }
    return geo;
  }

  sync(
    projectiles: ReadonlyArray<Projectile>,
    enemyProjectiles: ReadonlyArray<EnemyProjectile>,
    frameTime: number,
  ): void {
    this.syncPlayer(projectiles, frameTime);
    this.syncEnemy(enemyProjectiles, frameTime);
  }

  private syncPlayer(projectiles: ReadonlyArray<Projectile>, t: number): void {
    const alive = new Set<Projectile>();
    for (const p of projectiles) {
      alive.add(p);
      let entry = this.playerEntries.get(p);
      const half = halfExtentFor(p.kind, false);
      if (!entry) {
        entry = this.createEntry(half);
        this.playerEntries.set(p, entry);
      }
      this.baker.bake(entry.sprite, half, (scratch) => {
        const g = new Graphics();
        scratch.addChild(g);
        drawPlayerProjectile(g, p, t);
      });
      entry.mesh.position.set(p.x * TILE_SIZE, Y_OFFSET, p.y * TILE_SIZE);
    }
    this.pruneDead(this.playerEntries, alive);
  }

  private syncEnemy(projectiles: ReadonlyArray<EnemyProjectile>, t: number): void {
    const alive = new Set<EnemyProjectile>();
    for (const p of projectiles) {
      alive.add(p);
      let entry = this.enemyEntries.get(p);
      const half = halfExtentFor(p.kind, true);
      if (!entry) {
        entry = this.createEntry(half);
        this.enemyEntries.set(p, entry);
      }
      this.baker.bake(entry.sprite, half, (scratch) => {
        const g = new Graphics();
        scratch.addChild(g);
        drawEnemyProjectile(g, p, t);
      });
      entry.mesh.position.set(p.x * TILE_SIZE, Y_OFFSET, p.y * TILE_SIZE);
    }
    this.pruneDead(this.enemyEntries, alive);
  }

  private pruneDead<K>(map: Map<K, Entry>, alive: Set<K>): void {
    for (const [key, entry] of map) {
      if (alive.has(key)) continue;
      this.group.remove(entry.mesh);
      entry.material.dispose();
      this.baker.disposeSprite(entry.sprite);
      map.delete(key);
    }
  }

  private createEntry(half: number): Entry {
    const sprite = this.baker.createSprite(TEX_PX);
    const material = new THREE.MeshBasicMaterial({
      map: sprite.texture,
      transparent: true,
      depthWrite: false,
    });
    const geo = this.getGeometry(half);
    const mesh = new THREE.Mesh(geo, material);
    mesh.renderOrder = RENDER_ORDER;
    this.group.add(mesh);
    return { sprite, mesh, material, half };
  }

  dispose(): void {
    for (const entry of this.playerEntries.values()) {
      this.group.remove(entry.mesh);
      entry.material.dispose();
      this.baker.disposeSprite(entry.sprite);
    }
    this.playerEntries.clear();
    for (const entry of this.enemyEntries.values()) {
      this.group.remove(entry.mesh);
      entry.material.dispose();
      this.baker.disposeSprite(entry.sprite);
    }
    this.enemyEntries.clear();
    for (const geo of this.geoCache.values()) geo.dispose();
    this.geoCache.clear();
  }
}

// ── Local-space 2D draw ports (centered at origin) ───────────────

function drawPlayerProjectile(g: Graphics, p: Projectile, time: number): void {
  const t = Math.min(p.tier, 5) - 1;

  switch (p.kind) {
    case 'bullet': {
      const tailTiles = 0.5 + t * 0.15;
      const headR = 2 + t * 0.5;
      const dirX = p.vx / p.speed;
      const dirY = p.vy / p.speed;
      const perpX = -dirY;
      const perpY = dirX;
      const flicker = 0.85 + 0.15 * Math.sin(time * 40 + p.x * 9 + p.y * 13);
      const trailLen = tailTiles * TILE_SIZE;
      const segments = 4;

      // Layer 1: Wide soft glow trail
      {
        const hw = 2.5 + t * 0.8;
        g.moveTo(perpX * hw, perpY * hw);
        for (let i = 1; i <= segments; i++) {
          const frac = i / segments;
          const cx = -dirX * trailLen * frac;
          const cy = -dirY * trailLen * frac;
          const w = hw * (1 - frac * 0.9);
          g.lineTo(cx + perpX * w, cy + perpY * w);
        }
        for (let i = segments; i >= 0; i--) {
          const frac = i / segments;
          const cx = -dirX * trailLen * frac;
          const cy = -dirY * trailLen * frac;
          const w = hw * (1 - frac * 0.9);
          g.lineTo(cx - perpX * w, cy - perpY * w);
        }
        g.closePath();
        g.fill({ color: BULLET_GLOW[t]!, alpha: 0.25 * flicker });
      }
      // Layer 2: Bright core trail
      {
        const hw = 1.2 + t * 0.3;
        g.moveTo(perpX * hw, perpY * hw);
        for (let i = 1; i <= segments; i++) {
          const frac = i / segments;
          const cx = -dirX * trailLen * frac;
          const cy = -dirY * trailLen * frac;
          const w = hw * (1 - frac * 0.95);
          g.lineTo(cx + perpX * w, cy + perpY * w);
        }
        for (let i = segments; i >= 0; i--) {
          const frac = i / segments;
          const cx = -dirX * trailLen * frac;
          const cy = -dirY * trailLen * frac;
          const w = hw * (1 - frac * 0.95);
          g.lineTo(cx - perpX * w, cy - perpY * w);
        }
        g.closePath();
        g.fill({ color: BULLET_TRACER[t]!, alpha: 0.55 * flicker });
      }
      // Head outer glow
      g.circle(0, 0, headR + 3 + t * 0.6);
      g.fill({ color: BULLET_GLOW[t]!, alpha: 0.3 * flicker });
      // Head body
      g.circle(0, 0, headR);
      g.fill(BULLET_HEAD[t]);
      // White-hot core
      g.circle(0, 0, headR * 0.45);
      g.fill({ color: 0xffffff, alpha: 0.8 });
      // Tier 3+: forward energy spike
      if (t >= 2) {
        const spikeLen = 4 + t * 1.5;
        g.moveTo(dirX * spikeLen, dirY * spikeLen);
        g.lineTo(perpX * 1.2, perpY * 1.2);
        g.lineTo(-perpX * 1.2, -perpY * 1.2);
        g.closePath();
        g.fill({ color: BULLET_HEAD[t]!, alpha: 0.4 * flicker });
      }
      break;
    }
    case 'rocket': {
      const bodyLen = 4 + t * 1;
      const bodyW = 2 + t * 0.5;
      const dirX = p.vx / p.speed;
      const dirY = p.vy / p.speed;
      const perpX = -dirY;
      const perpY = dirX;
      const plumeLen = (0.5 + t * 0.2) * TILE_SIZE;
      const plumeBaseW = 2 + t * 1;
      const segments = 6;
      const flicker  = 0.85 + 0.15 * Math.sin(time * 30 + p.x * 7 + p.y * 11);
      const flicker2 = 0.9  + 0.10 * Math.sin(time * 45 + p.x * 13);

      const nozX = -dirX * bodyLen * 0.5;
      const nozY = -dirY * bodyLen * 0.5;

      // Layer 1: Outer smoke plume
      {
        const w = plumeBaseW * 1.6;
        g.moveTo(nozX + perpX * w, nozY + perpY * w);
        for (let i = 1; i <= segments; i++) {
          const frac = i / segments;
          const cx = nozX - dirX * plumeLen * frac;
          const cy = nozY - dirY * plumeLen * frac;
          const hw = w * (1 - frac * 0.85);
          const wobble = Math.sin(time * 20 + frac * 8) * frac * 1.5;
          g.lineTo(cx + perpX * hw + perpX * wobble, cy + perpY * hw + perpY * wobble);
        }
        for (let i = segments; i >= 0; i--) {
          const frac = i / segments;
          const cx = nozX - dirX * plumeLen * frac;
          const cy = nozY - dirY * plumeLen * frac;
          const hw = w * (1 - frac * 0.85);
          const wobble = Math.sin(time * 20 + frac * 8 + 3) * frac * 1.5;
          g.lineTo(cx - perpX * hw + perpX * wobble, cy - perpY * hw + perpY * wobble);
        }
        g.closePath();
        g.fill({ color: 0x665544, alpha: 0.2 * flicker });
      }
      // Layer 2: Orange fire plume
      {
        const w = plumeBaseW * 1.1;
        const len = plumeLen * 0.75;
        g.moveTo(nozX + perpX * w, nozY + perpY * w);
        for (let i = 1; i <= segments; i++) {
          const frac = i / segments;
          const cx = nozX - dirX * len * frac;
          const cy = nozY - dirY * len * frac;
          const hw = w * (1 - frac * 0.8);
          const wobble = Math.sin(time * 35 + frac * 10) * frac * 1.2;
          g.lineTo(cx + perpX * hw + perpX * wobble, cy + perpY * hw + perpY * wobble);
        }
        for (let i = segments; i >= 0; i--) {
          const frac = i / segments;
          const cx = nozX - dirX * len * frac;
          const cy = nozY - dirY * len * frac;
          const hw = w * (1 - frac * 0.8);
          const wobble = Math.sin(time * 35 + frac * 10 + 3) * frac * 1.2;
          g.lineTo(cx - perpX * hw + perpX * wobble, cy - perpY * hw + perpY * wobble);
        }
        g.closePath();
        g.fill({ color: 0xff6622, alpha: 0.45 * flicker });
      }
      // Layer 3: Hot yellow-white core
      {
        const w = plumeBaseW * 0.55;
        const len = plumeLen * 0.5;
        g.moveTo(nozX + perpX * w, nozY + perpY * w);
        for (let i = 1; i <= segments; i++) {
          const frac = i / segments;
          const cx = nozX - dirX * len * frac;
          const cy = nozY - dirY * len * frac;
          const hw = w * (1 - frac * 0.9);
          g.lineTo(cx + perpX * hw, cy + perpY * hw);
        }
        for (let i = segments; i >= 0; i--) {
          const frac = i / segments;
          const cx = nozX - dirX * len * frac;
          const cy = nozY - dirY * len * frac;
          const hw = w * (1 - frac * 0.9);
          g.lineTo(cx - perpX * hw, cy - perpY * hw);
        }
        g.closePath();
        g.fill({ color: 0xffdd88, alpha: 0.6 * flicker2 });
      }
      // Nozzle glow
      g.circle(nozX, nozY, plumeBaseW * 0.7 + t * 0.3);
      g.fill({ color: 0xffeecc, alpha: 0.35 * flicker });
      if (t >= 2) {
        g.circle(nozX, nozY, plumeBaseW * 1.2 + t * 0.5);
        g.fill({ color: 0xff8844, alpha: 0.12 * flicker });
      }
      // Rocket body (triangle)
      const tipX = dirX * bodyLen;
      const tipY = dirY * bodyLen;
      const rearLX = -dirX * bodyLen * 0.5 + perpX * bodyW;
      const rearLY = -dirY * bodyLen * 0.5 + perpY * bodyW;
      const rearRX = -dirX * bodyLen * 0.5 - perpX * bodyW;
      const rearRY = -dirY * bodyLen * 0.5 - perpY * bodyW;
      g.moveTo(tipX, tipY);
      g.lineTo(rearLX, rearLY);
      g.lineTo(rearRX, rearRY);
      g.closePath();
      g.fill(ROCKET_BODY[t]);
      g.circle(tipX, tipY, 1.5);
      g.fill(ROCKET_CORE[t]);
      break;
    }
    case 'bomb': {
      const progress = 1 - p.life / p.maxLife;
      const arc = Math.sin(progress * Math.PI) * p.arcHeight * TILE_SIZE * 0.8;
      const shellR = 3.5 + t * 0.8;
      const coreR = 1.5 + t * 0.4;
      const bombY = -arc; // local-Y (up in screen space, north in 3D)
      const flicker = 0.85 + 0.15 * Math.sin(time * 25 + p.x * 7 + p.y * 11);

      // Shadow on ground
      const shadowR = shellR * (0.6 + 0.4 * Math.sin(progress * Math.PI));
      g.circle(0, 1.5, shadowR);
      g.fill({ color: 0x000000, alpha: 0.25 });

      const urgency = progress * progress;
      const pulseR = shellR + 3 + Math.sin(time * 12 + progress * 20) * 1.5;
      g.circle(0, bombY, pulseR);
      g.fill({ color: BOMB_CORE[t]!, alpha: (0.08 + urgency * 0.12) * flicker });

      g.circle(0, bombY, shellR);
      g.fill(BOMB_SHELL[t]);
      g.circle(-shellR * 0.3, bombY - shellR * 0.3, shellR * 0.45);
      g.fill({ color: 0xffffff, alpha: 0.15 });

      g.circle(0, bombY, coreR);
      g.fill(BOMB_CORE[t]);
      g.circle(0, bombY, coreR * 0.5);
      g.fill({ color: 0xffffff, alpha: 0.4 * flicker });

      if (progress < 0.85) {
        const fuseTopY = bombY - shellR;
        const fuseLen = 4 + t;
        const fuseWave = Math.sin(time * 15) * 1.5;
        const fuseTipX = fuseWave;
        const fuseTipY = fuseTopY - fuseLen;

        g.moveTo(0, fuseTopY);
        g.lineTo(fuseTipX, fuseTipY);
        g.stroke({ color: 0x554433, width: 1.2 });

        const sparkFlicker = 0.7 + 0.3 * Math.sin(time * 50 + p.x * 5);
        g.circle(fuseTipX, fuseTipY, 2.5 + t * 0.3);
        g.fill({ color: BOMB_FUSE[t]!, alpha: 0.3 * sparkFlicker });
        g.circle(fuseTipX, fuseTipY, 1.2);
        g.fill({ color: 0xffffff, alpha: 0.8 * sparkFlicker });

        for (let s = 0; s < 2 + (t >= 2 ? 1 : 0); s++) {
          const sparkAngle = time * 20 + s * 2.1 + p.x * 3;
          const sparkDist = 2 + Math.sin(time * 30 + s * 4) * 1.5;
          const sx = fuseTipX + Math.cos(sparkAngle) * sparkDist;
          const sy = fuseTipY + Math.sin(sparkAngle) * sparkDist;
          g.circle(sx, sy, 0.6);
          g.fill({ color: 0xffff44, alpha: 0.5 * sparkFlicker });
        }
      }
      break;
    }
  }
}

function drawEnemyProjectile(g: Graphics, p: EnemyProjectile, time: number): void {
  if (p.kind === 'bomb') {
    const progress = 1 - p.life / p.maxLife;
    const arcY = -Math.sin(progress * Math.PI) * 2.0 * TILE_SIZE;
    const flicker = 0.85 + 0.15 * Math.sin(time * 30 + p.x * 11 + p.y * 7);
    const urgency = progress * progress;

    // Shadow
    const shadowR = 2 + Math.sin(progress * Math.PI) * 1.5;
    g.circle(0, 1.5, shadowR);
    g.fill({ color: 0x000000, alpha: 0.18 });

    const auraR = 7 + Math.sin(time * 15 + progress * 12) * 1.5;
    g.circle(0, arcY, auraR);
    g.fill({ color: 0x7722bb, alpha: (0.08 + urgency * 0.1) * flicker });

    g.circle(0, arcY, 5.5);
    g.fill({ color: 0xcc66ff, alpha: 0.2 * flicker });

    g.circle(0, arcY, 3.5);
    g.fill(0x9933cc);

    g.circle(-1, arcY - 1, 1.5);
    g.fill({ color: 0xeeccff, alpha: 0.3 });

    g.circle(0, arcY, 1.2);
    g.fill({ color: 0xddaaff, alpha: 0.6 * flicker });
  } else {
    const dirX = p.vx / p.speed;
    const dirY = p.vy / p.speed;
    const tailTiles = 0.3;
    const tx = -dirX * tailTiles * TILE_SIZE;
    const ty = -dirY * tailTiles * TILE_SIZE;
    const flicker = 0.85 + 0.15 * Math.sin(time * 45 + p.x * 13 + p.y * 9);

    g.moveTo(tx, ty); g.lineTo(0, 0);
    g.stroke({ color: 0xff0000, width: 4, alpha: 0.1 * flicker });
    g.moveTo(tx, ty); g.lineTo(0, 0);
    g.stroke({ color: 0xff4444, width: 1.8, alpha: 0.7 * flicker });
    g.moveTo(tx, ty); g.lineTo(0, 0);
    g.stroke({ color: 0xff8888, width: 0.8, alpha: 0.9 });

    g.circle(0, 0, 3.5);
    g.fill({ color: 0xff0000, alpha: 0.15 * flicker });
    g.circle(0, 0, 1.5);
    g.fill(0xff2222);
    g.circle(0, 0, 0.7);
    g.fill({ color: 0xffaaaa, alpha: 0.7 });
  }
}
